import { API_ENDPOINT } from "@/constants/appConstants";
import { PROFILE_PERFORMANCE_RECRUITER_ACTIONS_QUERY_KEY } from "@/lib/queryKeys";
import {
  type InfiniteData,
  type UseInfiniteQueryResult,
  useInfiniteQuery,
} from "@tanstack/react-query";

import {
  CategoryInfo,
  NotificationCategory,
  RecruiterActionsResponse,
  RecruiterNotification,
} from "../types";

export const getRecruiterActions = async <T>(
  start: number = 0,
  limit: number = 10,
  category: NotificationCategory = "ALL",
): Promise<T | Error> => {
  try {
    if (typeof window === "undefined") {
      throw new Error("Window is not available");
    }

    const response = await fetch(
      `${API_ENDPOINT(window.location.host).middleware.recruiterActions}?start=${start}&limit=${limit}&notificationType=${category}`,
      {
        method: "GET",
        credentials: "include",
      },
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data: T = await response.json();
    return data;
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    } else {
      throw new Error(
        `Error occurred while fetching recruiter actions data, ${JSON.stringify(error)}`,
      );
    }
  }
};

export type RecruiterActionsPage = {
  success: boolean;
  start: number;
  limit: number;
  total: number;
  categories: Record<NotificationCategory, CategoryInfo>;
  notifications: RecruiterNotification[];
};

const deriveCategories = (
  apiCategories: RecruiterActionsResponse["categories"] = {
    ALL: 0,
  } as RecruiterActionsResponse["categories"],
): Record<NotificationCategory, CategoryInfo> => {
  const categoryInfo: Record<NotificationCategory, CategoryInfo> = {
    ALL: {
      count: apiCategories.ALL ?? 0,
      notificationTypes: [],
    },
    PROFILE_VIEWED: {
      count: apiCategories.PROFILE_VIEWED ?? 0,
      notificationTypes: ["PROFILE_VIEWED"],
    },
    PROFILE_SAVED: {
      count: apiCategories.PROFILE_SAVED ?? 0,
      notificationTypes: ["PROFILE_SAVED"],
    },
    DOWNLOADED_CV: {
      count: apiCategories.DOWNLOADED_CV ?? 0,
      notificationTypes: ["DOWNLOADED_CV"],
    },
    PROFILE_CONTACT_VIEWED: {
      count: apiCategories.PROFILE_CONTACT_VIEWED ?? 0,
      notificationTypes: ["PROFILE_CONTACT_VIEWED"],
    },
    PROFILE_CONTACTED: {
      count: apiCategories.PROFILE_CONTACTED ?? 0,
      notificationTypes: ["PROFILE_CONTACTED"],
    },
  };

  return categoryInfo;
};

const mapNotifications = (
  notifications: RecruiterNotification[],
): RecruiterNotification[] => {
  return notifications.map((n) => ({
    ...n,
    company: {
      name: n.company?.name || "",
      logoUrl:
        n.company?.logoUrl ||
        "https://media.foundit.in/trex/public/default/images/career-services/prep-ai/default-company.svg",
    },
  }));
};

export const useRecruiterActions = (
  category: NotificationCategory = "ALL",
  limit: number = 10,
): UseInfiniteQueryResult<
  InfiniteData<RecruiterActionsPage, number>,
  Error
> => {
  return useInfiniteQuery<
    RecruiterActionsPage,
    Error,
    InfiniteData<RecruiterActionsPage, number>,
    [string, NotificationCategory, number],
    number
  >({
    queryKey: [
      PROFILE_PERFORMANCE_RECRUITER_ACTIONS_QUERY_KEY,
      category,
      limit,
    ],
    initialPageParam: 0,
    queryFn: ({ pageParam }) =>
      getRecruiterActions<RecruiterActionsResponse>(
        pageParam as number,
        limit,
        category,
      ).then((res) => {
        const apiData = res as RecruiterActionsResponse;
        const categories = deriveCategories(apiData.categories);
        const notifications = mapNotifications(apiData.notifications || []);

        return {
          success: apiData.success,
          start: apiData.start,
          limit: apiData.limit,
          total: apiData.total,
          categories,
          notifications,
        };
      }),
    getNextPageParam: (lastPage, allPages) => {
      const totalFetched = allPages.reduce(
        (acc, page) => acc + (page.notifications?.length || 0),
        0,
      );
      if (totalFetched >= lastPage.total) return undefined;
      return totalFetched;
    },
    retry: 1,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};
